import React, { useState } from 'react';
import { CheckIcon, ClockIcon, DownloadIcon, LoaderIcon, SearchXIcon } from 'lucide-react';
import { AdminEmptyState, AdminErrorState, AdminPageTitle, ConfirmationModal, Panel, PermissionGate, StatusBadge } from '../../components/admin/AdminPrimitives';
import { Skeleton } from '../../components/Skeleton';
import { Button } from '../../components/ui/Button';
import { useLocale } from '../../contexts/LocaleContext';

/**
 * Internal design-system reference for the reusable admin states.
 * Reachable only at /admin/estados — it is intentionally absent from the
 * sidebar so it never shows up in production navigation.
 */
export function AdminStateGallery() {
  const {
    t
  } = useLocale();
  const [modal, setModal] = useState(false);
  return <div className="space-y-4" data-id="element-1839" data-mp="2h015wscwoqs">
      <AdminPageTitle title={t('adm.states.title')} subtitle={t('adm.states.subtitle')} data-id="element-1840" data-mp="2h9ikvzc5nkv" />

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2" data-id="element-1841" data-mp="3tmrh63cf6kzv">
        <Panel title={t('adm.states.loading')} hint="AdminPrimitives · StateBlock" data-id="element-1842" data-mp="3tpncdjwvuvkm">
          <div className="flex flex-col items-center rounded-card border border-sand-line bg-white/70 px-4 py-8 text-center" data-id="element-1843" data-mp="hiul37j5jfcm">
            <LoaderIcon size={22} className="text-soft" aria-hidden="true" data-id="element-1844" data-mp="8jg20t5be725" />
            <p className="mt-2 text-[13px] font-medium text-deep" role="status" data-id="element-1845" data-mp="8hd13e2mitin">
              {t('adm.states.loading')}
            </p>
          </div>
        </Panel>

        <Panel title={t('adm.states.skeleton')} hint="Skeleton · DataTable loading" data-id="element-1846" data-mp="119g5qoo2xe3f">
          <div className="space-y-2" data-id="element-1847" data-mp="158ozvun17dzi">
            {Array.from({
            length: 5
          }).map((_, index) => <Skeleton key={index} className="h-[46px] w-full" data-id="element-1848" data-mp="r8w1re96dvhi" data-mpi={index} />)}
          </div>
        </Panel>

        <Panel title={t('adm.states.empty')} hint="AdminEmptyState" data-id="element-1849" data-mp="w7qsd2ddwoc6">
          <AdminEmptyState title={t('adm.table.empty')} body={t('adm.table.emptyBody')} data-id="element-1850" data-mp="3bzn1soklzk5n" />
        </Panel>

        <Panel title={t('adm.states.noResults')} hint="AdminEmptyState · search result" data-id="element-1851" data-mp="3h0uacpuksndd">
          <AdminEmptyState title={t('state.no-results.title')} body={t('state.no-results.body')} action={<Button variant="outline" data-id="element-1853" data-mp="2o4uuzzma6aca">
                <SearchXIcon size={15} data-id="element-1854" data-mp="2o3t8myzte1f9" />
                {t('adm.action.clear')}
              </Button>} data-id="element-1852" data-mp="2wvv6qqmev5md" />
        </Panel>

        <Panel title={t('adm.states.error')} hint="AdminErrorState" data-id="element-1855" data-mp="2j3njpqkxit0v">
          <AdminErrorState title={t('state.error.title')} body={t('state.error.body')} onRetry={() => undefined} data-id="element-1856" data-mp="2e88axd1s3nbb" />
        </Panel>

        <Panel title={t('adm.states.permission')} hint="PermissionGate" data-id="element-1857" data-mp="25577jk4asabc">
          <PermissionGate allowed={false} area={t('settings.security').toLowerCase()} data-id="element-1858" data-mp="2075kyp7nxsv4">
            <span data-id="element-1859" data-mp="3elx22k6xu3z" />
          </PermissionGate>
        </Panel>

        <Panel title={t('adm.states.session')} hint="AdminLogin · ?estado=expirada" data-id="element-1860" data-mp="3e3tslz42fmk">
          <div className="flex flex-col items-center rounded-card border border-deep bg-deep px-4 py-8 text-center" data-id="element-1861" data-mp="3um88wih2d2vx">
            <span className="grid h-12 w-12 place-items-center rounded-pill border border-gold/45 bg-deeper text-gold" data-id="element-1862" data-mp="5ys0m94w7ik0">
              <ClockIcon size={20} data-id="element-1863" data-mp="avs72l5nmnbn" />
            </span>
            <p className="mt-3 text-[15px] font-bold text-sand" data-id="element-1864" data-mp="fpwj3its1ljl">
              {t('state.session.title')}
            </p>
            <p className="mt-1 max-w-[36ch] text-[12.5px] leading-relaxed text-ondark" data-id="element-1865" data-mp="kn4yxcrgddho">
              {t('adm.login.expired')}
            </p>
          </div>
        </Panel>

        <Panel title={t('adm.states.exportProcessing')} hint="AdminExports · job status" data-id="element-1866" data-mp="3o5pvblh2sauu">
          <div className="flex items-center gap-2 rounded-card border border-sand-line bg-white/70 p-3" data-id="element-1867" data-mp="3t3hw74zp77b6">
            <LoaderIcon size={16} className="text-gold-deep" aria-hidden="true" data-id="element-1868" data-mp="1107xcwfbser" />
            <span className="flex-1 text-[12.5px] text-deep" data-id="element-1869" data-mp="18e24m7snnekh">
              {t('export.preparing')}
            </span>
            <StatusBadge status="processing" label={t('adm.states.exportProcessing')} data-id="element-1870" data-mp="1ce2nc4msebxc" />
          </div>
          <Skeleton className="mt-2 h-[10px] w-2/3" data-id="element-1871" data-mp="1ayq93oays5er" />
        </Panel>

        <Panel title={t('adm.states.exportDone')} hint="AdminExports · job status" data-id="element-1872" data-mp="15zdm93k3eyc6">
          <div className="flex items-center gap-2 rounded-card border border-gold/45 bg-white p-3" data-id="element-1873" data-mp="1kvuydx03wgzs">
            <CheckIcon size={16} className="text-soft" aria-hidden="true" data-id="element-1874" data-mp="1fxbbnudov16d" />
            <span className="flex-1 text-[12.5px] text-deep" data-id="element-1875" data-mp="1fz4gr58nldc9">{t('export.ready')}</span>
            <Button variant="outline" data-id="element-1876" data-mp="1tuzyv8v8akel">
              <DownloadIcon size={15} data-id="element-1877" data-mp="1oxzssxydphnh" />
              {t('account.export')}
            </Button>
          </div>
        </Panel>

        <Panel title={t('adm.states.success')} hint="StateBlock · dark tone" data-id="element-1878" data-mp="23lksehw1oyqn">
          <div className="flex flex-col items-center rounded-card border border-deep bg-deep px-4 py-8 text-center" data-id="element-1879" data-mp="1ymzskjkpgh2q">
            <span className="grid h-12 w-12 place-items-center rounded-pill border border-gold/45 bg-deeper text-gold" data-id="element-1880" data-mp="2coc4mjwqvyjc">
              <CheckIcon size={20} data-id="element-1881" data-mp="28na665anjqif" />
            </span>
            <p className="mt-3 text-[15px] font-bold text-sand" data-id="element-1882" data-mp="2r9ho8w9962oh">
              {t('state.success.title')}
            </p>
            <p className="mt-1 text-[12.5px] text-ondark" data-id="element-1883" data-mp="2w81bczzl1kif">{t('state.success.body')}</p>
          </div>
        </Panel>

        <Panel title={t('adm.states.destructive')} hint="ConfirmationModal · danger" data-id="element-1884" data-mp="2i8t73sokbm7d">
          <p className="text-[12.5px] leading-relaxed text-muted" data-id="element-1885" data-mp="2n733u7pxquv3">
            Ações destrutivas usam o preto da marca, nunca o dourado, e exigem confirmação
            digitada.
          </p>
          <Button variant="danger" className="mt-3" onClick={() => setModal(true)} data-id="element-1886" data-mp="2db9s2qk1j5ed">
            {t('adm.states.openModal')}
          </Button>
        </Panel>
      </div>

      <ConfirmationModal open={modal} destructive title={t('adm.states.destructive')} body={t('adm.permission.body', {
      area: t('settings.security').toLowerCase()
    })} confirmLabel={t('adm.action.confirm')} requireText="CONFIRMAR" onConfirm={() => setModal(false)} onClose={() => setModal(false)} data-id="element-1887" data-mp="23k5dvt26vokl" />
    </div>;
}